import './MenuList.scss';

import React, { useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import type { MenuListComponentProps } from 'react-select';

import type { DropdownOption, DropdownOptionGroup } from '../../../../common/Dropdown';
import Loader from '../../../../common/Loader';
import bem from '../../../../utils/bem';

type MenuListProps<T extends boolean = true> = MenuListComponentProps<DropdownOption, T, DropdownOptionGroup>;

const [, element] = bem('dropdown-list');

function MenuList<T extends boolean>({
  options,
  selectProps,
  children,
  innerRef,
  isMulti,
  getValue,
  maxHeight,
}: MenuListProps<T>): JSX.Element {
  const { handleSelectAll, handleClearAll, multipleSelectControlsInMenu } = selectProps;
  const selectedCount = multipleSelectControlsInMenu ? getValue().length : 0;
  const hasValue = selectedCount > 0;

  const isSelectAllVisible = useMemo(() => {
    if (!multipleSelectControlsInMenu || !handleSelectAll) return false;
    const allOptions = selectProps.options ?? options;
    const totalOptions = allOptions
      ? allOptions
          .map((option: DropdownOption | DropdownOptionGroup) => ('options' in option ? option.options : [option]))
          .flat().length
      : 0;
    return totalOptions !== selectedCount;
  }, [multipleSelectControlsInMenu, handleSelectAll, selectProps.options, options, selectedCount]);

  const showControls = multipleSelectControlsInMenu && (isSelectAllVisible || hasValue);

  if (showControls) {
    return (
      <div className={element('menu-list-with-controls')} style={{ maxHeight }}>
        <div className={element('menu-controls')} data-testid="menu-list-controls">
          {isSelectAllVisible && (
            <button
              type="button"
              data-testid="select-all-control"
              className={element('menu-controls-button')}
              onClick={handleSelectAll}
            >
              Select All
            </button>
          )}
          {isSelectAllVisible && hasValue && ' / '}
          {hasValue && handleClearAll && (
            <button
              type="button"
              data-testid="clear-all-control"
              className={element('menu-controls-button')}
              onClick={handleClearAll}
            >
              Clear All
            </button>
          )}
        </div>
        <div
          id="scrollable-options"
          data-testid="scrollable-options"
          className={element('menu-list', isMulti ? 'is-multi' : '')}
          ref={innerRef}
        >
          <InfiniteScroll
            scrollableTarget="scrollable-options"
            dataLength={options.length}
            loader={<Loader />}
            next={selectProps.onNext}
            hasMore={selectProps.hasMore}
          >
            {children}
          </InfiniteScroll>
        </div>
      </div>
    );
  }

  return (
    <div
      id="scrollable-options"
      data-testid="scrollable-options"
      className={element('menu-list', isMulti ? 'is-multi' : '')}
      ref={innerRef}
    >
      <InfiniteScroll
        scrollableTarget="scrollable-options"
        dataLength={options.length}
        loader={<Loader />}
        next={selectProps.onNext}
        hasMore={selectProps.hasMore}
      >
        {children}
      </InfiniteScroll>
    </div>
  );
}

export default MenuList;
